import { createServerFn } from "@tanstack/react-start";

export const getUpcomingEvents = createServerFn({ method: "GET" }).handler(async () => {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const today = new Date().toISOString().slice(0, 10);
  const { data, error } = await supabaseAdmin
    .from("events")
    .select("id,title,event_date,event_time,location,poster_url,description")
    .gte("event_date", today)
    .order("event_date", { ascending: true })
    .limit(50);
  if (error) { console.error("getUpcomingEvents:", error); throw new Error("Failed to load events"); }
  return data ?? [];
});

export const getGallery = createServerFn({ method: "GET" }).handler(async () => {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { data, error } = await supabaseAdmin
    .from("gallery")
    .select("id,image_url,thumbnail_url,caption,sort_order,likes_count,shares_count")
    .order("sort_order", { ascending: true })
    .order("created_at", { ascending: false })
    .limit(100);
  if (error) { console.error("getGallery:", error); throw new Error("Failed to load gallery"); }
  return data ?? [];
});

export const getGalleryPage = createServerFn({ method: "GET" })
  .inputValidator((input: { offset?: number; limit?: number }) => ({
    offset: Math.max(0, Math.floor(input?.offset ?? 0)),
    limit: Math.min(48, Math.max(1, Math.floor(input?.limit ?? 12))),
  }))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const from = data.offset;
    const to = data.offset + data.limit - 1;
    const { data: rows, error, count } = await supabaseAdmin
      .from("gallery")
      .select("id,image_url,thumbnail_url,caption,sort_order", { count: "exact" })
      .order("sort_order", { ascending: true })
      .order("created_at", { ascending: false })
      .range(from, to);
    if (error) { console.error("getGalleryPage:", error); throw new Error("Failed to load gallery"); }
    const total = count ?? 0;
    const nextOffset = from + (rows?.length ?? 0);
    return {
      items: rows ?? [],
      nextOffset: nextOffset < total ? nextOffset : null,
      total,
    };
  });

// Booked dates for calendar (only confirmed bookings — date only, no PII)
export const getBookedDates = createServerFn({ method: "GET" }).handler(async () => {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const today = new Date().toISOString().slice(0, 10);
  const horizon = new Date();
  horizon.setMonth(horizon.getMonth() + 3);
  const { data, error } = await supabaseAdmin
    .from("bookings")
    .select("event_date")
    .eq("status", "confirmed")
    .gte("event_date", today)
    .lte("event_date", horizon.toISOString().slice(0, 10));
  if (error) { console.error("getBookedDates:", error); throw new Error("Failed to load availability"); }
  return (data ?? []).map((r) => r.event_date as string);
});

export const getAdvertisements = createServerFn({ method: "GET" }).handler(async () => {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { data, error } = await supabaseAdmin
    .from("advertisements")
    .select("id,image_url,caption,link_url,sort_order")
    .eq("active", true)
    .order("sort_order", { ascending: true })
    .order("created_at", { ascending: false })
    .limit(20);
  if (error) { console.error("getAdvertisements:", error); throw new Error("Failed to load advertisements"); }
  return data ?? [];
});

export const getFacebookPosts = createServerFn({ method: "GET" }).handler(async () => {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { data, error } = await supabaseAdmin
    .from("facebook_posts")
    .select("id,post_url,is_video,sort_order,created_at")
    .order("sort_order", { ascending: true })
    .order("created_at", { ascending: false })
    .limit(3);
  if (error) { console.error("getFacebookPosts:", error); throw new Error("Failed to load facebook posts"); }
  return data ?? [];
});

export const getGalleryPageSize = createServerFn({ method: "GET" }).handler(async () => {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { data } = await supabaseAdmin
    .from("site_settings")
    .select("value")
    .eq("key", "gallery_page_size")
    .maybeSingle();
  const raw = Number((data as any)?.value ?? 6);
  return Math.min(48, Math.max(1, Number.isFinite(raw) ? raw : 6));
});

export const getSiteLogo = createServerFn({ method: "GET" }).handler(async () => {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { data } = await supabaseAdmin
    .from("site_settings")
    .select("value")
    .eq("key", "header_logo_url")
    .maybeSingle();
  const raw = (data as any)?.value;
  const url = typeof raw === "string" ? raw : (raw && typeof raw === "object" && typeof raw.url === "string" ? raw.url : "");
  return url || null;
});
